import React, { useState, useEffect } from 'react';
import './NextEvent.css';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../util/routes';
import EventStatus, { eventStatusLabel } from '../status/EventStatus';
import { useContextMenu } from '../context/ContextMenuContext';
import { getMeta } from '../../offline/db';
import { eventRules } from '../../util/eventRules';
import { releaseSummary } from '../../util/participantGroups';
import { addOnColorStyle, hasAddOnColor } from '../../util/addOns';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import Tooltip from '../tooltip/Tooltip';

function formatTimeRange(time, timeEnd) {
  const fmt = t => t ? t.substring(0, 5) : null;
  const from = fmt(time);
  const to   = fmt(timeEnd);
  if (!from) return null;
  return to ? `${from} – ${to} Uhr` : `${from} Uhr`;
}

function formatOfflineStand(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
    + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function CapBar({ value, max, label }) {
  if (!max) return null;
  const pct = Math.min(100, Math.round((value / max) * 100));
  const cls = pct >= 100 ? 'full' : pct >= 80 ? 'warn' : '';
  return (
    <div className="nextEventCapBar">
      <div className="nextEventCapBarTrack">
        <div className={`nextEventCapBarFill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="nextEventCapBarLabel">{label} — {value}/{max} ({pct} %)</span>
    </div>
  );
}

function stripeClass(state) {
  switch (state) {
    case 'Draft':     return 'yellow';
    case 'MemberPreview':
    case 'Published': return 'blue';
    case 'Completed': return 'green';
    case 'Cancelled': return 'red';
    default:          return '';
  }
}

// Tile of one sign-up add-on: name (ellipsis, full text + numbers in the hover tooltip — the card itself is a link,
// so no tap tooltip) and used/max ("/max" only with a limit, MaxQuantity 0 = unlimited). The icon takes the add-on
// color (AddOn.colorKey); once the limit is reached the value turns to the warning colour and the icon drops the
// add-on color (warning wins, like the exhausted counter pills).
function AddOnStat({ addOn }) {
  const used = addOn.used ?? 0;
  const max = addOn.maxQuantity ?? 0;
  const exhausted = max > 0 && used >= max;
  const colored = !exhausted && hasAddOnColor(addOn.colorKey);
  const tooltip = max > 0
    ? `${addOn.name}: ${used} von ${max} gebucht${exhausted ? ' – ausgebucht' : ''}`
    : `${addOn.name}: ${used} gebucht (unbegrenzt)`;
  return (
    <div className="nextEventStat">
      <Tooltip text={tooltip} className="nextEventStatTip">
        <span className="nextEventStatLabel">
          <span
            className={`material-symbols-rounded nextEventStatLabelIcon${colored ? ' isAddOnColored' : ''}`}
            style={colored ? addOnColorStyle(addOn.colorKey) : undefined}
          >
            inventory_2
          </span>
          <span className="nextEventStatLabelText">{addOn.name}</span>
        </span>
      </Tooltip>
      <span className={`nextEventStatValue${exhausted ? ' isFull' : ''}`}>
        {used}
        {max > 0 && <span className="nextEventStatMax">/{max}</span>}
      </span>
    </div>
  );
}

export default function NextEvent({ post }) {
  const { openContextMenu } = useContextMenu();
  const { byId: groupsById } = useParticipantGroups();
  const [longPressTimer, setLongPressTimer] = useState(null);

  // Offline-Status der lokalen Kopie: Eine Kopie existiert nur, wenn der Nutzer Spieltagleiter ist
  // und den Spieltag schon einmal online geöffnet hat -> Pill nur dann sichtbar.
  const [offlineStand, setOfflineStand] = useState(null);
  const postId = post ? post.id : null;
  useEffect(() => {
    let active = true;
    if (postId) {
      getMeta(`event:${postId}`)
        .then(m => { if (active) setOfflineStand(m && m.fetchedAt ? m.fetchedAt : null); })
        .catch(() => {});
    } else {
      setOfflineStand(null);
    }
    return () => { active = false; };
  }, [postId]);

  const handleContextMenu = (e) => { if (post) openContextMenu(e, post.id, 'nextevent'); };
  const handleTouchStart  = (e) => setLongPressTimer(setTimeout(() => handleContextMenu(e), 500));
  const handleTouchMove   = ()  => { if (longPressTimer) clearTimeout(longPressTimer); };
  const handleTouchEnd    = ()  => { if (longPressTimer) clearTimeout(longPressTimer); };

  if (!post) {
    return (
      <div className="nextEventEmpty">
        <span className="material-symbols-rounded nextEventEmptyIcon">event_busy</span>
        <p>Keine bevorstehenden Events</p>
      </div>
    );
  }

  const date       = new Date(post.date);
  const day        = date.getDate();
  const month      = date.toLocaleString('de', { month: 'long' });
  const timeRange  = formatTimeRange(post.time, post.timeEnd);
  const confirmed  = post.confirmedCount  ?? 0;
  const maxPlayers = post.maxPlayers;
  // One tile per sign-up add-on of the process (EventAddOnSummary, process-row order); events without process
  // have none.
  const addOns     = Array.isArray(post.addOns) ? post.addOns : [];
  // Kind from the template (EventLabel/Icon).
  const rules      = eventRules(post);
  const typeLabel  = rules.eventLabel ?? 'Event';
  const typeIcon   = rules.icon ?? 'event';
  // "Freigegeben für Mitglieder" statt "Member-Vorschau", sobald Gruppen-Freigaben vorliegen.
  const releasedFor = releaseSummary(post.participantGroupSettings, groupsById);
  const statusLabel = eventStatusLabel(post.state, { event: post, releasedFor });

  return (
    <Link
      to={ROUTES.event(post.id, post.date)}
      className="nextEventCard"
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={`nextEventStripe ${stripeClass(post.state)}`} />

      <div className="nextEventBody">
        {/* Left: date */}
        <div className="nextEventDateCol">
          <span className="nextEventDay">{day}</span>
          <span className="nextEventMonth">{month}</span>
          {timeRange && <span className="nextEventTime">{timeRange}</span>}
        </div>

        {/* Right: stats */}
        <div className="nextEventInfo">
          <div className="nextEventBadgeRow">
            <span className="nextEventTypeBadge">
              <span className="material-symbols-rounded" style={{ fontSize: 14 }}>{typeIcon}</span>
              {typeLabel}
            </span>
            {offlineStand && (
              <span className="nextEventOfflinePill">
                <span className="material-symbols-rounded nextEventOfflinePillIcon">cloud_done</span>
                Offline bereit · {formatOfflineStand(offlineStand)}
              </span>
            )}
          </div>

          <div className="nextEventStats">
            <div className="nextEventStat">
              <span className="nextEventStatLabel">
                <span className="material-symbols-rounded nextEventStatLabelIcon">person</span>
                <span className="nextEventStatLabelText">Teilnehmer</span>
              </span>
              <span className="nextEventStatValue">
                {confirmed}
                {maxPlayers > 0 && <span className="nextEventStatMax">/{maxPlayers}</span>}
              </span>
            </div>
            {addOns.map(a => <AddOnStat key={a.addOnId} addOn={a} />)}
          </div>

          {maxPlayers > 0 && (
            <CapBar value={confirmed} max={maxPlayers} label="Spieler" />
          )}
        </div>
      </div>

      <div className="nextEventFooter">
        <div className="nextEventStatusRow">
          <EventStatus statusId={post.state} event={post} releasedFor={releasedFor} />
          {statusLabel && <span className="nextEventStatusLabel">{statusLabel}</span>}
        </div>
        <span className="nextEventOpenLink">
          Öffnen
          <span className="material-symbols-rounded nextEventOpenLinkIcon">arrow_forward</span>
        </span>
      </div>
    </Link>
  );
}
